// ---------------------------------------------------------------------------
// CreateDatasourceForm — inline form for creating a new datasource.
// ---------------------------------------------------------------------------

import { useState } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import { useStudioLocale } from '@/i18n'
import { cn } from '@/lib/utils'

const SOURCE_TYPES = ['rest', 'graphql', 'static', 'fetch', 'import', 'api-client', 'webmcp'] as const
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const

interface Props {
  onClose: () => void
  defaultBlockName?: string
}

/**
 * Reads a datasource from the SAME place the Sources list and the inspector
 * read it — `currentProject.blocks[block].data` — rather than from this
 * form's own render-time snapshot. Both the pre-check and the post-write
 * read-back below go through here on purpose: the gate for "did the create
 * land / would it destroy something" has to be derived from the consumer of
 * the name, not from the fact that the handler ran.
 */
function readDatasource(blockName: string, name: string): unknown {
  const blocks = useStudioStore.getState().currentProject?.blocks as
    | Record<string, { data?: Record<string, unknown> } | undefined>
    | undefined
  return blocks?.[blockName]?.data?.[name]
}

export function CreateDatasourceForm({ onClose, defaultBlockName }: Props) {
  const project = useStudioStore((s) => s.currentProject)
  const addDatasource = useStudioStore((s) => s.addDatasource)
  const { strings } = useStudioLocale()
  const blocks = project ? Object.keys(project.blocks) : []

  const [name, setName] = useState('')
  const [sourceType, setSourceType] = useState<string>('rest')
  const [endpoint, setEndpoint] = useState('')
  const [method, setMethod] = useState<string>('GET')
  const [blockName, setBlockName] = useState(defaultBlockName ?? blocks[0] ?? '')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      toast.error('Datasource name is required')
      return
    }
    if (!blockName) {
      toast.error('Select a block')
      return
    }

    // COLLISION REFUSAL. `addDatasource` is an UPSERT — `block.data[name] =
    // source` — and it has to stay one: the undo `restoreDatasource` path and
    // the Import path both depend on writing over an existing key. So the
    // refusal belongs here, on the affordance that says "New": before this
    // guard, retyping an existing name replaced that datasource's whole
    // config and still toasted "created", destroying the original with no
    // receipt (slice-5A §A3-b / D8). Fail closed and name the conflict; the
    // existing datasource is editable from the inspector.
    if (readDatasource(blockName, trimmed) !== undefined) {
      toast.error(strings.dataExplorer.datasourceNameTaken(trimmed, blockName))
      return
    }

    const config: Record<string, unknown> = ['fetch', 'import', 'api-client', 'webmcp'].includes(sourceType)
      ? { kind: sourceType }
      : { type: sourceType }
    if (sourceType === 'rest' || sourceType === 'graphql') {
      if (endpoint.trim()) config.endpoint = endpoint.trim()
      config.method = method
    }

    addDatasource(blockName, trimmed, config)

    // Read back before claiming success — `addDatasource` silently returns
    // when the block is gone (a stale `blockName` after a concurrent block
    // delete/rename), which would otherwise toast "created" over nothing.
    // Same shape as the Expression Lab's `apply()`.
    if (readDatasource(blockName, trimmed) === undefined) {
      toast.error(
        strings.dataExplorer.datasourceCreateFailed(trimmed, blockName),
      )
      return
    }
    toast.success(strings.dataExplorer.datasourceCreated(trimmed))
    onClose()
  }

  return (
    <form data-testid="create-datasource-form" onSubmit={handleSubmit} className="px-3 py-2 space-y-2 border-b border-border bg-accent/30">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">New Datasource</span>
        <button data-testid="create-datasource-cancel" type="button" onClick={onClose} className="text-[10px] text-muted-foreground hover:text-foreground">Cancel</button>
      </div>

      {blocks.length > 1 && (
        <select
          data-testid="create-datasource-block-select"
          value={blockName}
          onChange={(e) => setBlockName(e.target.value)}
          className="w-full h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        >
          {blocks.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      )}

      <input
        data-testid="create-datasource-name-input"
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Datasource name"
        className="w-full h-7 rounded border border-border bg-background px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
      />

      <div className="flex gap-2">
        <select
          data-testid="create-datasource-type-select"
          value={sourceType}
          onChange={(e) => setSourceType(e.target.value)}
          className="flex-1 h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        >
          {SOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {(sourceType === 'rest' || sourceType === 'graphql') && (
          <select
            data-testid="create-datasource-method-select"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="w-20 h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {HTTP_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
      </div>

      {sourceType !== 'static' && (
        <input
          data-testid="create-datasource-endpoint-input"
          value={endpoint}
          onChange={(e) => setEndpoint(e.target.value)}
          placeholder="Endpoint URL"
          className="w-full h-7 rounded border border-border bg-background px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
        />
      )}

      <button
        data-testid="create-datasource-submit"
        type="submit"
        className={cn(
          'w-full h-7 rounded text-xs font-medium transition-colors',
          name.trim()
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-muted-foreground cursor-not-allowed',
        )}
        disabled={!name.trim()}
      >
        Create Datasource
      </button>
    </form>
  )
}
